// **The C-2 instruct wire** (`demo-presentation-shell` tasks 10.1/10.2, contract C-2). A pure
// module — request-body assembly, response parsing, and the response's commands mapped onto this
// change's own {@link DemoBeeCommand} vocabulary — kept separate from `InstructPromptBar.tsx`'s
// React state machine so the wire shape is unit-testable without a DOM.
//
// Mirrors `test/fake-instruct-transport.ts`'s wire shapes in field names only (never imported from
// there — that file is test-only): this is where **this production code's own** assumed C-2 shape
// lives, exactly as `stage-driver.ts` is for C-1's vocabulary. A field renamed on the real server
// route is a decode failure here, once task 13.3 swaps the fake transport for the real endpoint.

import type { DemoBeeCommand, DemoInstructWorldRead, DemoPoint } from './stage-driver';

export const INSTRUCT_PATH = '/demo/instruct';

export type InstructCommandName = 'move_to' | 'collect_nectar' | 'drop_nectar' | 'fight' | 'resume';

export interface InstructCommandWire {
  bee_ids: number[];
  command: InstructCommandName;
  target?: DemoPoint;
  patch_id?: number;
  /** `collect_nectar`'s errand flag — the agent's answer to "grab some nectar and bring it back
   * to the hive" as **one** command per bee (E2E round-3 finding G). */
  then_deliver?: boolean;
}

/**
 * One **completed turn** of retained context: what the presenter said and what the agent answered.
 *
 * Not a role-tagged message — the server's `WireTranscriptTurn` is `{text, reply}` and its request
 * struct is `deny_unknown_fields`, so a role-tagged entry refused the whole request as `malformed`
 * the moment the transcript stopped being empty (found at task 13.3, i.e. on the second instruct
 * of the chapter, which is not a failure the first rehearsal would have shown either).
 */
export interface InstructTranscriptEntry {
  text: string;
  reply: string;
}

export interface InstructRequestBody {
  idempotency_key: string;
  text: string;
  field_size_u: number;
  hive: DemoPoint;
  roster: { id: number; type: string; x: number; y: number; carrying: boolean }[];
  patches: { id: number; x: number; y: number; has_nectar: boolean }[];
  transcript: InstructTranscriptEntry[];
}

/** A world read used when the driver does not (yet) implement {@link DemoInstructWorldRead} —
 * `worldForInstruct` is optional (`stage-driver.ts`'s own doc comment), and an instruct call must
 * still be possible against an empty world rather than refusing to build a request at all. */
const EMPTY_WORLD: DemoInstructWorldRead = { fieldSizeU: 0, hive: { x: 0, y: 0 }, roster: [], patches: [] };

export function buildInstructRequestBody(args: {
  idempotencyKey: string;
  text: string;
  world?: DemoInstructWorldRead;
  transcript: readonly InstructTranscriptEntry[];
}): InstructRequestBody {
  const world = args.world ?? EMPTY_WORLD;
  return {
    idempotency_key: args.idempotencyKey,
    text: args.text,
    field_size_u: world.fieldSizeU,
    hive: world.hive,
    roster: world.roster.map((bee) => ({ id: bee.id, type: bee.type, x: bee.x, y: bee.y, carrying: bee.carrying })),
    patches: world.patches.map((patch) => ({ id: patch.id, x: patch.x, y: patch.y, has_nectar: patch.hasNectar })),
    transcript: [...args.transcript],
  };
}

export interface InstructBeeCommand {
  readonly beeIds: readonly number[];
  readonly command: DemoBeeCommand;
}

/** Maps C-2's wire commands onto this change's own {@link DemoBeeCommand} union — the same shape
 * the point-and-click chapter's right-click already submits through
 * `DemoStageDriver.submitCommand` (design DD6: "returned commands ... dispatched through the same
 * override surface the right-click chapter uses"). A command the server sent with a shape this
 * client cannot express (a `move_to` with no target, a `collect_nectar` with no patch) is skipped
 * rather than dispatched malformed — C-2's own validation (programme page §C-2) is what is
 * trusted to have kept this from happening in practice. */
export function toBeeCommands(commands: readonly InstructCommandWire[]): readonly InstructBeeCommand[] {
  const out: InstructBeeCommand[] = [];
  for (const wire of commands) {
    const command = toDemoBeeCommand(wire);
    if (command !== null) {
      out.push({ beeIds: wire.bee_ids, command });
    }
  }
  return out;
}

function toDemoBeeCommand(wire: InstructCommandWire): DemoBeeCommand | null {
  switch (wire.command) {
    case 'move_to':
      return wire.target === undefined ? null : { command: 'move_to', target: wire.target };
    case 'collect_nectar':
      return wire.patch_id === undefined
        ? null
        : {
            command: 'collect_nectar',
            patchId: wire.patch_id,
            ...(wire.then_deliver === true ? { thenDeliver: true } : {}),
          };
    case 'drop_nectar':
      return { command: 'drop_nectar' };
    case 'fight':
      return { command: 'fight' };
    case 'resume':
      return { command: 'resume' };
    default:
      return null;
  }
}

// -- response bodies -------------------------------------------------------------------------

export interface InstructSuccessBody {
  status: 'ok';
  commands: InstructCommandWire[];
  reply: string;
}

/**
 * The **one** refusal envelope, exactly as `waggle-server`'s `WireAnswer` serialises it: two
 * statuses only, with the specific failure named in `reason` and the fresh key in snake_case
 * `retry_key`. Corrected at task 13.3 — this client previously expected `status: "timeout"` and
 * `status: "budget_exhausted"` as their own arms with a camelCase `retryKey`, so against the real
 * route every timeout and every exhausted budget decoded as a bare refusal (the presenter got the
 * wrong message) and the retry key was always `''`.
 */
export interface InstructRefusedBody {
  status: 'refused';
  reason: string;
  retry_key?: string;
}

/** The two `reason` values the surface gives their own message and affordance to. */
const TIMEOUT_REASON = 'timeout';
const BUDGET_EXHAUSTED_REASON = 'budget_exhausted';

/** What one call resolves to, once the response's status line and body have both been read. */
export type InstructOutcome =
  | { kind: 'ok'; commands: InstructCommandWire[]; reply: string }
  | { kind: 'refused'; reason: string }
  | { kind: 'timeout'; retryKey: string }
  | { kind: 'budgetExhausted' }
  | { kind: 'malformed' }
  | { kind: 'unavailable'; message: string };

/** One call, start to finish: builds the request, posts it, and interprets the response —
 * never throws. A transport failure (`fetchImpl` rejecting) and a body that will not parse as
 * JSON both become {@link InstructOutcome} values, the same "refusals are values" discipline the
 * rest of the rounds client keeps. */
export async function postInstruct(args: {
  fetchImpl: typeof fetch;
  baseUrl?: string;
  body: InstructRequestBody;
}): Promise<InstructOutcome> {
  let response: Response;
  try {
    // **No capability header.** `WAGGLE_DEMO_KEY` used to ride `Authorization` here, which was
    // doubly wrong on a gated deployment: the route is under `API_PREFIXES` and wanted the tenant's
    // token in that same header. The key is gone; the tenant is the boundary.
    response = await args.fetchImpl(`${args.baseUrl ?? ''}${INSTRUCT_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args.body),
    });
  } catch (error) {
    return { kind: 'unavailable', message: error instanceof Error ? error.message : 'the request failed' };
  }
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return { kind: 'malformed' };
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return { kind: 'malformed' };
  }
  const body = parsed as Record<string, unknown>;
  switch (body.status) {
    case 'ok':
      return {
        kind: 'ok',
        commands: Array.isArray(body.commands) ? (body.commands as InstructCommandWire[]) : [],
        reply: typeof body.reply === 'string' ? body.reply : '',
      };
    case 'refused': {
      const reason = typeof body.reason === 'string' ? body.reason : 'refused';
      if (reason === TIMEOUT_REASON) {
        return { kind: 'timeout', retryKey: typeof body.retry_key === 'string' ? body.retry_key : '' };
      }
      if (reason === BUDGET_EXHAUSTED_REASON) {
        return { kind: 'budgetExhausted' };
      }
      return { kind: 'refused', reason };
    }
    default:
      return { kind: 'malformed' };
  }
}
